import { useState, useEffect } from "react";
import {
  EnvelopeIcon,
  PhoneIcon,
  ArrowLongLeftIcon,
  ArrowLongRightIcon,
} from "@heroicons/react/20/solid";
import image2 from "../images/logo.png";
import Cookies from 'js-cookie';

const Filters = [
  "All",
  "Tunis",
  "Ariana",
  "Ben Arous",
  "Manouba",
  "Nabeul",
  "Zaghouan",
  "Bizerte",
  "Béja",
  "Jendouba",
  "Kef",
  "Siliana",
  "Kairouan",
  "Kasserine",
  "Sidi Bouzid",
  "Sousse",
  "Monastir",
  "Mahdia",
  "Sfax",
  "Gafsa",
  "Tozeur",
  "Kebili",
  "Gabès",
  "Medenine",
  "Tataouine",
];

export default function NotaireList() {
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [notaires, setNotaires] = useState([]);
  const [currentNPage, setCurrentNPage] = useState(1);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [iframeSrc, setIframeSrc] = useState("");
  const [currentPaginationSet, setCurrentPaginationSet] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const itemsPerPage = 20;
  const pagesPerSet = 15;

  const fetchNotaires = async () => {
    setIsLoading(true);
    try {
      const token = Cookies.get("token");
      if (selectedFilter === null) {
        return [];
      }
      const url = `${process.env.REACT_APP_ADDRESS}/notaire/notaires/${selectedFilter}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (response.status === 200) {
        return data.notaires;
      } else if (response.status === 404) {
        return [];
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error("Error fetching notaires:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchNotairesData = async () => {
      const notairesData = await fetchNotaires();
      setNotaires(notairesData || []);
    };
    fetchNotairesData();
  }, [selectedFilter]);

  useEffect(() => {
    if (selectedPerson !== null) {
      const encodedAddress = encodeURIComponent(selectedPerson.Adresse);
      setIframeSrc(
        `https://maps.google.com/maps?width=520&height=400&hl=en&q=${encodedAddress}&t=&z=14&ie=UTF8&iwloc=B&output=embed`
      );
    }
  }, [selectedPerson]);

  const handleFilterChange = (event) => {
    setSelectedFilter(event.target.value);
  };

  const toggleModal = (person) => {
    setSelectedPerson(person);
    setIsModalOpen(!isModalOpen);
  };

  const totalNPages = Math.ceil((notaires?.length || 0) / itemsPerPage);
  const currentNotaires = notaires?.slice(
    (currentNPage - 1) * itemsPerPage,
    currentNPage * itemsPerPage
  ) || [];

  const nextPage = () =>
    setCurrentNPage((prevPage) => Math.min(prevPage + 1, totalNPages));
  const prevPage = () =>
    setCurrentNPage((prevPage) => Math.max(prevPage - 1, 1));

  const nextPaginationSet = () =>
    setCurrentPaginationSet((prevSet) => prevSet + 1);
  const prevPaginationSet = () =>
    setCurrentPaginationSet((prevSet) => Math.max(prevSet - 1, 0));

  const paginationStart = currentPaginationSet * pagesPerSet;
  const paginationEnd = Math.min(paginationStart + pagesPerSet, totalNPages);

  return (
    <>
      <div className="p-1">
        <div className="p-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Notary Finder
          </h1>
          <div className="bg-white shadow-lg rounded-lg p-8 mb-10">
            <h2 className="text-2xl font-semibold text-pink-600 mb-4">
              What Is It:
            </h2>
            <p className="text-gray-700 mb-6">
              Need a notary to finalize your tawkil? Our convenient Notary
              Finder feature helps you locate a notary public near your
              location.
            </p>
          </div>
        </div>
        <div>
          <div className="flex justify-end mt-4">
            <select
              value={selectedFilter}
              onChange={handleFilterChange}
              className=" block w-32 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-pink-500 focus:border-pink-500"
            >
              {Filters.map((filter, index) => (
                <option key={index} value={filter}>
                  {filter}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div>
        {isLoading ? (
          <div role="status" className="flex justify-center mt-12">
            <svg
              aria-hidden="true"
              className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-pink-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        ) : (
          <>
            <ul
              role="list"
              className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-12"
            >
              {currentNotaires.map((person) => (
                <li
                  key={person.email}
                  className="col-span-1 divide-y divide-gray-200 rounded-lg bg-white shadow-lg"
                >
                  <div className="flex flex-col items-center justify-between space-y-4 p-6">
                    <img
                      className="h-20 w-20 flex-shrink-0 rounded-full bg-gray-100"
                      src={image2}
                      alt="profile"
                    />
                    <div className="flex-1 text-center">
                      <h3 className="truncate text-lg font-medium text-gray-900">
                        {person.Nom}
                      </h3>
                      <div className="mt-2 text-sm text-gray-500">
                        <span className="font-semibold">Address: </span>
                        <span className="inline-flex flex-shrink-0 items-center rounded-full bg-green-50 px-2 py-1 text-sm font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                          {person.Adresse}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-gray-500">
                        <span className="font-semibold">Phone: </span>
                        {Array.isArray(person.Téléphone)
                          ? person.Téléphone.join(" / ")
                          : person.Téléphone}
                      </div>
                    </div>
                    <div className="flex w-full divide-x divide-gray-200">
                      <button
                        onClick={() => toggleModal(person)}
                        className="flex w-1/2 items-center justify-center gap-x-2 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900 hover:bg-gray-100"
                      >
                        <EnvelopeIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                        View Details
                      </button>
                      <a
                        href={`tel:${person.telephone}`}
                        className="flex w-1/2 items-center justify-center gap-x-2 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900 hover:bg-gray-100"
                      >
                        <PhoneIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                        Call
                      </a>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <nav className="flex items-center justify-between border-t border-gray-200 px-4 sm:px-0">
              <div className="-mt-px flex w-0 flex-1">
                <button
                  onClick={prevPage}
                  disabled={currentNPage === 1}
                  className={`inline-flex items-center border-t-2 border-transparent pr-1 pt-4 text-sm font-medium ${
                    currentNPage === 1
                      ? "text-gray-300 pointer-events-none"
                      : "text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  }`}
                >
                  <ArrowLongLeftIcon
                    className="mr-3 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  Previous
                </button>
              </div>
              <div className="hidden md:-mt-px md:flex">
                {paginationStart > 0 && (
                  <button
                    onClick={prevPaginationSet}
                    className="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  >
                    Less
                  </button>
                )}
                {Array.from({ length: paginationEnd - paginationStart }).map(
                  (_, index) => (
                    <a
                      key={index + paginationStart + 1}
                      href="#"
                      onClick={() =>
                        setCurrentNPage(index + paginationStart + 1)
                      }
                      className={`inline-flex items-center border-t-2 px-4 pt-4 text-sm font-medium ${
                        currentNPage === index + paginationStart + 1
                          ? "border-pink-500 text-pink-600"
                          : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                      }`}
                    >
                      {index + paginationStart + 1}
                    </a>
                  )
                )}
                {paginationEnd < totalNPages && (
                  <button
                    onClick={nextPaginationSet}
                    className="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  >
                    More
                  </button>
                )}
              </div>
              <div className="-mt-px flex w-0 flex-1 justify-end">
                <button
                  onClick={nextPage}
                  disabled={currentNPage === totalNPages}
                  className={`inline-flex items-center border-t-2 border-transparent pl-1 pt-4 text-sm font-medium ${
                    currentNPage === totalNPages
                      ? "text-gray-300 pointer-events-none"
                      : "text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  }`}
                >
                  Next
                  <ArrowLongRightIcon
                    className="ml-3 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </nav>
          </>
        )}
        {selectedPerson && isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
            <div className="bg-pink-100 rounded-lg p-8 max-w-lg w-full relative">
              <button
                onClick={() => toggleModal(null)}
                className="absolute top-0 right-0 mt-2 mr-2 text-gray-600 hover:text-gray-800 focus:outline-none"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <h2 className="text-lg font-semibold text-pink-800">
                {selectedPerson.Nom}
              </h2>
              <p className="text-gray-600">{selectedPerson.Adresse}</p>
              <div className="w-full">
                <iframe
                  width="520"
                  height="400"
                  frameBorder="0"
                  scrolling="no"
                  marginHeight="0"
                  marginWidth="0"
                  src={iframeSrc}
                  className="w-full h-96"
                >
                  <a href="https://www.gps.ie/">gps systems</a>
                </iframe>
              </div>
              <div className="mt-4 flex flex-col space-y-2">
                <button
                  onClick={() =>
                    window.open(`mailto:${selectedPerson.email}`, "_blank")
                  }
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-semibold text-white bg-pink-600 hover:bg-pink-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-600"
                >
                  Email
                </button>
                <button
                  onClick={() =>
                    window.open(`tel:${selectedPerson.telephone}`, "_blank")
                  }
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-semibold text-white bg-pink-600 hover:bg-pink-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-600"
                >
                  Call
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
